import { scrubContactTokens } from "../../../contact-token-scrub.mjs";
import { isDeploySkewMessage } from "./deploy-skew-recovery.ts";

type SentryFrame = { filename?: string };
type SentryException = {
    value?: string;
    stacktrace?: { frames?: SentryFrame[] };
};
type SentryEventLike = { exception?: { values?: SentryException[] } };

const BUNDLED_SCRIPT = /\/assets\/[^/?#]+\.m?js(?:[?#]|$)/u;

const IN_APP_BROWSER_BRIDGE_MESSAGES = new Set([
    "Error invoking postMessage: Java object is gone",
]);

const INJECTED_SCRIPT_MESSAGES = new Set([
    "undefined is not an object (evaluating 'window._META.IABInnerFrame')",
    "undefined is not an object (evaluating 'window.webkit.messageHandlers')",
    "Script message handler is unavailable",
    "Invalid call to runtime.sendMessage(). Tab not found.",
]);

const UPSTREAM_SDK_MESSAGES = new Set([
    "Called on script loaded before session recording is available",
]);

function frames(exception: SentryException): SentryFrame[] {
    return exception.stacktrace?.frames ?? [];
}

function hasBundledFrame(exception: SentryException): boolean {
    return frames(exception).some(
        (frame) =>
            frame.filename !== undefined && BUNDLED_SCRIPT.test(frame.filename),
    );
}

function isThirdPartyException(exception: SentryException): boolean {
    const message = exception.value ?? "";
    if (UPSTREAM_SDK_MESSAGES.has(message)) return true;
    if (hasBundledFrame(exception)) return false;
    if (INJECTED_SCRIPT_MESSAGES.has(message)) return true;
    return (
        IN_APP_BROWSER_BRIDGE_MESSAGES.has(message) &&
        frames(exception).some((frame) =>
            frame.filename?.startsWith("iabjs://"),
        )
    );
}

export function isThirdPartyNoise(event: SentryEventLike): boolean {
    const exceptions = event.exception?.values ?? [];
    return exceptions.length > 0 && exceptions.every(isThirdPartyException);
}

export function createClientBeforeSend(recoverFromDeploySkew: () => boolean) {
    return <T extends SentryEventLike>(event: T): T | null => {
        if (isThirdPartyNoise(event)) return null;
        const deploySkew = event.exception?.values?.some((exception) =>
            isDeploySkewMessage(exception.value),
        );
        if (deploySkew && recoverFromDeploySkew()) return null;
        return scrubContactTokens(event);
    };
}
